import { v4 as uuid } from 'uuid';

import { LabelSizes, LabelTypes } from '@eika/label';

import { Link } from '../../defaultConfig';
import type { LoanApplication, LoanApplicationStatus } from '../../generated-types';
import TranslationsConfigType from '../../translations/translationType';

export const getTotalLoanApplicationCount = (applications: LoanApplication[]) =>
    applications
        .map((application) => Math.max(application.numberOfTasks ?? 0, 1))
        .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

export const getLoanApplicationLink = (
    application: LoanApplication,
    loanLinkBase: Link | undefined,
): Link | undefined => {
    if (!loanLinkBase) return undefined;
    switch (application.status) {
        case 'ACTIVE':
        case 'WAITING_FOR_CO_BORROWER':
        case 'WAITING_FOR_YOUR_APPROVAL':
            return { ...loanLinkBase, url: `${loanLinkBase.url}#/paabegyntSoeknad/${application.id.value}` };
        default:
            break;
    }
    switch (application.type) {
        case 'HOUSE_LOAN_INCREASE':
        case 'MOVING':
        case 'HOUSE_LOAN':
        case 'NEW_LOAN':
            return { ...loanLinkBase, url: `${loanLinkBase.url}#/boliglaan/${application.id.value}` };
        case 'PROOF_OF_FINANCING':
            return { ...loanLinkBase, url: `${loanLinkBase.url}#/finansieringsbevis/${application.id.value}` };
        default:
            return loanLinkBase;
    }
};

const getLabelAndBackgroundFromApplicationStatus = (
    status: LoanApplicationStatus,
): {
    labelType: LabelTypes;
    size: LabelSizes;
    attentionColor?: 'success' | 'danger';
} => {
    switch (status) {
        case 'APPROVED':
        case 'PAID_OUT':
        case 'BEING_PREPARED_FOR_PAYMENT':
            return { labelType: 'inverted', size: 'small', attentionColor: 'success' };
        case 'REFUSED':
            return { labelType: 'inverted', size: 'small', attentionColor: 'danger' };
        case 'CLOSED':
            return { labelType: 'default', size: 'small' };
        case 'ACTIVE':
        case 'APPLICATION_BEING_PROCESSED':
        case 'WAITING_FOR_CO_BORROWER':
        case 'WAITING_FOR_YOUR_APPROVAL':
        default:
            return { labelType: 'warning', size: 'small' };
    }
};

type ExtendedLoanApplication = {
    labels: { id: string; text: string; size: LabelSizes; type: LabelTypes }[];
    link: Link | undefined;
    attentionColor?: 'success' | 'danger';
} & LoanApplication;

export const mapLoanApplications =
    (translated: TranslationsConfigType, loanLink: Link | undefined) =>
    (applications: LoanApplication[]): ExtendedLoanApplication[] =>
        applications.map((application) => {
            const { labelType, attentionColor, size } = getLabelAndBackgroundFromApplicationStatus(application.status);
            return {
                ...application,
                link: getLoanApplicationLink(application, loanLink),
                attentionColor,
                labels: [
                    {
                        id: uuid(),
                        text: translated.loanApplications.status[application.status],
                        type: labelType,
                        size,
                    },
                    ...(typeof application.numberOfTasks === 'number' && application.numberOfTasks > 0
                        ? [
                              {
                                  id: uuid(),
                                  text: translated.loanApplications.needsAction,
                                  size,
                                  type: 'attention' as LabelTypes,
                              },
                          ]
                        : []),
                ],
            };
        });
